import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { HttpClient } from '@angular/common/http';
import { environment } from "../../environment/environment";
import { UserData } from '../models/user-data.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userDataKey = 'userData';

  private userDataSubject = new BehaviorSubject<UserData | null>(null);
  public userData$ = this.userDataSubject.asObservable();

  private authState = new BehaviorSubject<boolean>(false);

  constructor(private keycloakService: KeycloakService, private http: HttpClient) {
    this.restoreSession();
  }

  async fetchUserDataFromBackend(): Promise<void> {
    try {
      const token = await this.keycloakService.getToken();
      if (!token) {
        this.logout();
        return;
      }

      const res = await firstValueFrom(this.http.get<UserData>(`${environment.apiUrl}/usuario/`));

      this.userDataSubject.next(res);
      localStorage.setItem(this.userDataKey, JSON.stringify(res));
      this.authState.next(true);
    } catch (error) {
      console.error('Falha ao buscar dados do usuário do backend:', error);
      this.logout();
    }
  }

  getUserData(): UserData | null {
    return this.userDataSubject.getValue();
  }

  /** Sincroniza userData a partir do localStorage (ex.: após atualização de avatar no perfil). */
  syncUserDataFromStorage(): void {
    const storedData = localStorage.getItem(this.userDataKey);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData) as UserData;
        this.userDataSubject.next(parsedData);
      } catch (e) {
        console.error('Erro ao sincronizar userData:', e);
      }
    }
  }

  logout(): void {
    this.userDataSubject.next(null);
    localStorage.removeItem(this.userDataKey);
    this.authState.next(false);
  }

  isAuthenticated(): boolean {
    return !!this.userDataSubject.getValue();
  }

  private restoreSession(): void {
    const storedData = localStorage.getItem(this.userDataKey);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData) as UserData;
        this.userDataSubject.next(parsedData);
        this.authState.next(true);
      } catch (e) {
        console.error('Erro ao recuperar sessão:', e);
        this.logout();
      }
    }
  }

  async getKeycloakToken(): Promise<string> { return this.keycloakService.getToken(); }
  getAuthState(): Observable<boolean> { return this.authState.asObservable(); }
  getUserName(): string | null { 
    const tokenParsed = this.keycloakService.getKeycloakInstance().idTokenParsed; 
    return tokenParsed?.['name'] || tokenParsed?.['given_name'] || null; 
  }
}